import { CloseX } from './close-icon.js';
import { Icon } from './icon.js';
import { useContext, useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { CreateAccountContext } from './create-context.js';
import { formatMoney, sub, ZERO, type Account, type Money } from '@skoka/core';
import { AccountGlyph } from './account-glyph.js';
import { accountGroups } from './account-groups.js';
import { isNewCategory, PART_MIXED, PART_OPTIONS, PART_QUESTION, PART_TIP_SHEET, PART_TIP_CATEGORY, type Category } from './categories.js';
import { flash } from './flash.js';
import { getAll } from './db.js';
import { saveCategory, deleteCategory, categoryDeletable, saveCategoryOrder, sortCategories } from './category-store.js';
import { categoryKey, categoryDepth, descendants, frequentCategories, type CategoryKind } from './category-tree.js';
import { SheetWrap } from './sheet.js';
import { CardInput, CardLook, CardPick } from './card-fields.js';
import { Tip } from './tip.js';
import { CategoryGlyph, colorOf, type CategoryColor } from './category-icon.js';

/** Остаток счёта в строке выбора. Минус у кредитки — долг, а не деньги: так и называем. */
export const accountBalanceText = (a: Account): string => (a.kind !== 'credit_card' ? formatMoney(a.balance)
  : a.balance < ZERO ? `Долг ${formatMoney(sub(ZERO, a.balance))}`
  : `Баланс карты ${formatMoney(a.balance)}`);

export function AccountField({ label, pickerTitle, accounts, value, onChange, trigger }: {
  label: string; accounts: Account[]; value: string; onChange: (id: string) => void;
  /** Заголовок списка по роли счёта: «Списать со счёта», «Зачислить на счёт». */
  pickerTitle?: string;
  /** Своя кнопка вместо строки формы — например, строка карточки чека по макету. */
  trigger?: (open: () => void, current: Account | undefined) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const create = useContext(CreateAccountContext);
  const current = accounts.find(a => a.id === value);
  /*
   * Остаток счёта — само число, без слов «На счёте».
   *
   * Подпись повторяла то, что и так ясно из места: строка называется именем
   * счёта, под ней стоит сумма. На узких карточках перевода эти два слова
   * съедали место у длинного названия. У кредитной карты подпись остаётся:
   * там число со знаком минус — задолженность, а не деньги на счёте, и
   * одинаковое с дебетовой строкой оформление читалось бы как остаток.
   */
  const balance = accountBalanceText;
  /*
   * Список — группами, как на «Счетах»: карты, наличные, накопительные,
   * кредитки, в порядке человека и с суммой группы. Одна группа — без
   * заголовка: он ничего не добавил бы.
   */
  const groups = accountGroups(accounts);
  const row = (a: Account) => <button className="li w" key={a.id} onClick={() => { onChange(a.id); setOpen(false); }} aria-pressed={a.id === value}>
    <AccountGlyph account={a} size="s" /><span className="nm"><span className="account-name">{a.name}</span><span>{balance(a)}</span></span><span>{a.id === value ? <Icon name="check" size={14} /> : null}</span>
  </button>;
  return <>
    {trigger ? trigger(() => setOpen(true), current) : <button className="oprow accountfield" onClick={() => setOpen(true)} aria-label={`${label}: ${current?.name ?? 'Выбрать'}`}>
      <span className="field-label">{label}</span><span className="accountvalue"><span className="accountname">{current ? <AccountGlyph account={current} size="f" /> : <span className="account-symbol" aria-hidden="true"><Icon name="credit-card" size={16} /></span>}<span>{current?.name ?? 'Выбрать счёт'}</span><span aria-hidden="true"><Icon name="chevron-right" size={13} /></span></span><small>{current ? balance(current) : 'Счёт не выбран'}</small></span>
    </button>}
    {open && <SheetWrap title={pickerTitle ?? label} onClose={() => setOpen(false)}><div className="fieldlist account-list">
      {groups.length > 1
        ? groups.map((g) => <div key={g.key}>
          <div className="ops-group grp"><span>{g.title}</span><span className="num">{formatMoney(g.total)}</span></div>
          {g.accounts.map(row)}
        </div>)
        : accounts.map(row)}
      {!accounts.length && !create && <p>Нет подходящих счетов. Добавь счёт в «Деньгах» — «+» у «Моих денег».</p>}
      {/* Нужного счёта нет — заводим здесь же, не бросая начатую запись. */}
      {create && <button className="li w newrow" onClick={() => { setOpen(false); setCreating(true); }}>
        <span className="catico c-gray s-s" aria-hidden="true"><Icon name="plus" size={14} /></span><span className="nm"><span className="account-name">Новый счёт</span></span>
      </button>}
    </div></SheetWrap>}
    {creating && create?.((acc) => { setCreating(false); if (acc) onChange(acc.id); })}
  </>;
}

/**
 * Справочник категорий: список, поиск и правка.
 *
 * Список — не свалка из тридцати строк. Две части плана стоят наверху
 * сворачиваемыми группами, внутри них — дерево до трёх уровней, и у каждой
 * строки есть значок и цвет: в списке такого размера человек находит нужное
 * по форме и цвету, а не вчитываясь в названия.
 *
 * Поиск стоит первым и оформлен тихо: он служебный, а не главное действие
 * экрана. Переключатель «Расходы / Доходы» — вкладки, как у формы операции, и
 * он ничего не меняет молча: выбрать категорию чужого типа нельзя, тип
 * операции меняется только человеком.
 */
/** Сколько своих записей нужно, чтобы показывать «Часто используемые», а не «Популярные». */
const FRESH_UNTIL = 5;
/** Популярные категории нового человека — ключи стартового набора. */
const POPULAR: Record<CategoryKind, string[]> = {
  expense: ['groceries', 'cafe', 'transport', 'health', 'home', 'fun'],
  income: ['income.0', 'income.1', 'income.5'],
};

/**
 * Быстрые категории — те же, что «Часто используемые» в выборе категории:
 * по истории записей, а пока её мало — популярные (27.10.2026: ряд плиток
 * в форме записи). Уже выбранная, если её нет среди частых, — первой.
 * За ними — остальные категории верхнего уровня по порядку справочника:
 * ряд листается (28.10.2026).
 */
export function quickCategories(
  categories: Category[], facts: Array<{ type: string; categoryId?: string; occurredAt?: string }>,
  kind: CategoryKind, selected: string, count: number,
): Category[] {
  const frequent = frequentCategories(categories, facts, kind);
  const fresh = facts.filter((t) => t.type === kind && t.categoryId).length < FRESH_UNTIL;
  const pool = fresh
    ? [...frequent, ...POPULAR[kind].map((id) => categories.find((c) => c.id === id))
      .filter((c): c is Category => !!c && !c.archived && !frequent.some((f) => f.id === c.id))]
    : frequent;
  const rest = categories.filter((c) => !c.archived && !c.parentId && (c.kind ?? 'expense') === kind);
  const picked = categories.find((c) => c.id === selected && !c.archived);
  const ordered = [...pool, ...rest];
  const list = picked && !ordered.slice(0, count).some((c) => c.id === picked.id) ? [picked, ...ordered] : ordered;
  return list.filter((c, i, all) => all.findIndex((x) => x.id === c.id) === i).slice(0, count);
}

export function CategoryField({ categories, kind, value, onChange, onUpdated, manage = false, manageHead, trigger, label = 'Категория', anyLabel, otherKindNote, part: pickedPart, onPart, isNew = false }: {
  categories: Category[]; kind: CategoryKind; value: string;
  /** Категорию только что создали по фразе — пометка «новая», как у чеков. */
  isNew?: boolean;
  onChange: (id: string) => void; onUpdated: (categories: Category[]) => void; manage?: boolean;
  /** Строка над списком расходов в управлении — настройка новых категорий. */
  manageHead?: ReactNode;
  /**
   * Строка «выбрано всё» над списком — у фильтра, а не у записи: в
   * «Аналитике» выбор категории снимают тем же листом, каким ставят.
   */
  anyLabel?: string;
  /** Почему нельзя выбрать категорию другого типа — словами того места, откуда открыли. */
  otherKindNote?: string;
  /**
   * Фильтр по части плана целиком: тап по «Повседневные нужды» или «Свободные
   * траты» отбирает всю группу. Только у фильтра — запись идёт в категорию.
   */
  part?: 'need' | 'want' | null;
  onPart?: (part: 'need' | 'want') => void;
  /** Подпись строки формы: у возврата это «За что вернули». */
  label?: string;
  /** Своя кнопка вместо строки формы — например, плашка категории у позиции чека. */
  trigger?: (open: () => void, current: Category | undefined) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [viewKind, setViewKind] = useState(kind);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [name, setName] = useState('');
  const [part, setPart] = useState<Category['defaultPart']>('need');
  const [parentId, setParentId] = useState('');
  const [icon, setIcon] = useState('cart-shopping');
  const [color, setColor] = useState<CategoryColor>('green');
  /*
   * Пока цвет не выбран руками, он следует группе: зелёное — повседневные
   * нужды, розовое — свободные траты. Иначе новая категория в свободных
   * тратах оставалась зелёной, и список врал о том, к чему она относится.
   */
  const [colorPicked, setColorPicked] = useState(false);
  const [iconPicker, setIconPicker] = useState(false);
  /**
   * Выбор родителя — шагом в этом же листе, тем же списком категорий, что
   * и выбор категории (правило владельца 02.11.2026: есть лист — его и
   * переиспользуем). Прежде был отдельный плоский список «Авто / Штрафы».
   */
  const [pickParent, setPickParent] = useState(false);
  const [archive, setArchive] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  /** Удаление пройдёт: категория пустая. Иначе в форме только «Архивировать». */
  const [deletable, setDeletable] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [facts, setFacts] = useState<Array<{type: string; categoryId?: string; occurredAt?: string}>>([]);
  useEffect(() => { if (open) void getAll<{type: string; categoryId?: string; occurredAt?: string}>('transactions').then(setFacts).catch(() => setFacts([])); }, [open]);
  const current = categories.find(c => c.id === value);
  const list = sortCategories(categories.filter(c => (c.kind ?? 'expense') === viewKind && !!c.archived === (pickParent ? false : archive)));
  /** Порядок: тянут за полоски, как счета (28.10.2026). */
  const [reorder, setReorder] = useState(false);
  /** Открытое меню трёх точек. */
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [order, setOrder] = useState<string[]>([]);
  const box = useRef<HTMLDivElement>(null);
  const latestOrder = useRef(order);
  latestOrder.current = order;
  const archivedCount = categories.filter(c => (c.kind ?? 'expense') === viewKind && c.archived).length;
  /*
    Блок сверху — те же быстрые категории, что в записи: свои частые, пока
    своих мало — популярные, остаток — по порядку списка. Десять, пятью в
    ряд, как в записи (решение владельца 02.11.2026): шесть плиток вмещали мало.
  */
  const fresh = facts.filter((t) => t.type === viewKind && t.categoryId).length < FRESH_UNTIL;
  const quick = quickCategories(categories, facts, viewKind, value, 10);
  const switchSet = (set: Set<string>, key: string) => { const next = new Set(set); if (next.has(key)) next.delete(key); else next.add(key); return next; };
  const start = (c: Category | 'new') => {
    setEditing(c);
    setPickParent(false);
    setName(c === 'new' ? search : c.name);
    setPart(c === 'new' ? 'need' : c.defaultPart);
    setParentId(c === 'new' ? '' : c.parentId ?? '');
    setIcon(c === 'new' ? (viewKind === 'income' ? 'money-bill' : 'cart-shopping') : c.icon || 'circle-ellipsis');
    setColor(colorOf(c === 'new' ? { defaultPart: 'need', kind: viewKind } as Category : c));
    setColorPicked(c !== 'new' && !!c.color);
    setIconPicker(false); setDeleteConfirm(false); setError('');
    setDeletable(false);
    if (c !== 'new') void categoryDeletable(c.id).then(setDeletable).catch(() => setDeletable(false));
  };
  // Новая категория проверки ещё не в справочнике — это не повод сбросить выбор.
  const refresh = async () => { const next = sortCategories(await getAll<Category>('categories')); onUpdated(next); if (value && !isNewCategory(value) && (!next.some(c => c.id === value) || next.find(c => c.id === value)?.archived)) onChange(''); };
  const persist = async (archived?: boolean) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const c = await saveCategory({ ...(editing && editing !== 'new' ? { id: editing.id } : {}), name, kind: viewKind, defaultPart: part, parentId: parentId || null, icon, color, ...(archived === undefined ? {} : { archived }) });
      await refresh();
      if (!c.archived && editing === 'new' && viewKind === kind && !manage) { onChange(c.id); setOpen(false); }
      setEditing(null);
    } catch(e) { setError(e instanceof Error ? e.message : 'Не удалось сохранить'); }
    finally { setBusy(false); }
  };
  /** Архив из меню — сразу, без формы; ветка уходит вместе с категорией, как из формы. */
  const archiveNow = async (c: Category, archived: boolean) => {
    if (busy) return;
    setBusy(true);
    try {
      await saveCategory({ id: c.id, name: c.name, kind: viewKind, defaultPart: c.defaultPart, parentId: c.parentId, icon: c.icon, ...(c.color ? { color: c.color } : {}), archived });
      await refresh();
      flash(archived ? `«${c.name}» — в архиве` : `«${c.name}» восстановлена`);
    } catch (e) { flash(e instanceof Error ? e.message : 'Не удалось'); }
    finally { setBusy(false); setMenuFor(null); }
  };
  const remove = async () => {
    if (!editing || editing === 'new' || busy) return;
    setBusy(true); setError('');
    try { await deleteCategory(editing.id); await refresh(); setEditing(null); }
    catch(e) { setError(e instanceof Error ? e.message : 'Не удалось удалить'); }
    finally { setBusy(false); }
  };
  // Фильтр («Аналитика») отбирает по любой категории — и по группе из другой
  // части плана, и по архивной: история трат держится на ней.
  const filterMode = !!anyLabel;
  const choose = (c: Category) => {
    if (pickParent) { if (parentIds.has(c.id)) { setParentId(c.id); setPickParent(false); setSearch(''); } return; }
    if (reorder) return; if (manage) start(c); else if ((!c.archived || filterMode) && viewKind === kind) { onChange(c.id); setOpen(false); } };
  const editingBranch = editing && editing !== 'new' ? descendants(categories, editing.id) : new Set<string>();
  const parents = categories.filter(c => (c.kind ?? 'expense') === viewKind && !c.archived && !editingBranch.has(c.id) && categoryDepth(categories, c.id) < 3);
  const parentIds = new Set(parents.map(c => c.id));
  const parentCat = categories.find(c => c.id === parentId);
  /** Открыть выбор родителя: ветка выбранного раскрыта, чтобы его было видно. */
  const openParentPick = () => {
    const next = new Set(expanded);
    if (parentCat) next.add(parentCat.id);
    for (let p = parentCat?.parentId, seen = 0; p && seen < 5; p = categories.find(c => c.id === p)?.parentId, seen++) next.add(p);
    setExpanded(next); setSearch(''); setMenuFor(null); setPickParent(true);
  };
  /** Подпись строки — имена вложенных категорий: это и есть её содержание. */
  const childNames = (id: string) => list.filter(c => c.parentId === id).map(c => c.name).join(', ');
  /*
    Часть плана — меткой у строки (28.10.2026). Прежде список делился на
    «Повседневные нужды», «Свободные траты» и «Выбирать при записи», а дерево
    шло поперёк: «Дом» стоял в каждой части, где есть его подкатегория, серым
    и с подписью «Группа». Теперь дерево одно — каждая категория один раз.
  */
  const PART_TAG: Record<'need' | 'want' | 'mixed', [string, CategoryColor]> = {
    need: ['нужды', 'green'], want: ['свободные', 'pink'], mixed: ['при записи', 'gray'],
  };
  const shown = reorder ? order.map(id => list.find(c => c.id === id)).filter((c): c is Category => !!c) : list;
  const roots = shown.filter(c => !c.parentId || !shown.some(p => p.id === c.parentId));
  const moveTo = (id: string, parent: string, at: number) => setOrder(prev => {
    const parentOf = (x: string) => list.find(c => c.id === x)?.parentId ?? 'root';
    const sibs = prev.filter(x => parentOf(x) === parent);
    if (sibs.indexOf(id) === at) return prev;
    const rest = prev.filter(x => x !== id);
    const others = sibs.filter(x => x !== id);
    const anchor = others[at];
    const i = anchor ? rest.indexOf(anchor) : others.length ? rest.indexOf(others[others.length - 1]!) + 1 : rest.length;
    return [...rest.slice(0, i), id, ...rest.slice(i)];
  });
  const finishDrag = () => { setDrag(null); void saveCategoryOrder(latestOrder.current).then(refresh); };
  const grip = (c: Category) => {
    const parent = c.parentId && shown.some(p => p.id === c.parentId) ? c.parentId : 'root';
    return {
      className: 'grip', 'aria-label': `Передвинуть «${c.name}»`,
      onPointerDown: (e: React.PointerEvent<HTMLButtonElement>) => { e.preventDefault(); e.currentTarget.setPointerCapture(e.pointerId); setDrag(c.id); },
      onPointerMove: (e: React.PointerEvent<HTMLButtonElement>) => {
        if (drag !== c.id || !box.current) return;
        // Место — по середине соседей того же уровня: сколько их выше пальца, таков и номер.
        const others = [...box.current.querySelectorAll<HTMLElement>(`[data-list="${parent}"]`)].filter(el => el.dataset.id !== c.id);
        moveTo(c.id, parent, others.filter(el => { const r = el.getBoundingClientRect(); return r.top + r.height / 2 < e.clientY; }).length);
      },
      onPointerUp: finishDrag, onPointerCancel: finishDrag,
    };
  };
  /** Три точки — действия, а не сразу правка (решение владельца 28.10.2026).
      Новую категорию проверки ещё нечего править: её нет в справочнике. */
  const actions = (c: Category) => isNewCategory(c.id) ? <span className="category-edit" aria-hidden="true" /> : <>
    <button className="category-edit" aria-label={`Действия: ${c.name}`} aria-haspopup="menu" aria-expanded={menuFor === c.id} onClick={() => setMenuFor(menuFor === c.id ? null : c.id)}><DotsIcon /></button>
    {menuFor === c.id && <>
      <div className="catmenu-back" onClick={() => setMenuFor(null)} />
      <div className="catmenu" role="menu">
        <button role="menuitem" onClick={() => { setMenuFor(null); start(c); }}><Icon name="pen" size={16} />Изменить</button>
        <button role="menuitem" disabled={busy} onClick={() => void archiveNow(c, !c.archived)}><Icon name={c.archived ? 'rotate-left' : 'box-archive'} size={16} />{c.archived ? 'Восстановить' : 'Архивировать'}</button>
      </div>
    </>}
  </>;
  const row = (c: Category, depth: number, path: Set<string>): ReactNode => {
    if (path.has(c.id) || depth > 3) return null;
    const nextPath = new Set(path).add(c.id);
    const children = shown.filter(x => x.parentId === c.id);
    const isOpen = reorder || expanded.has(c.id);
    const note = children.length > 0 ? childNames(c.id) : '';
    const tag = viewKind === 'expense' ? PART_TAG[c.defaultPart === 'need' || c.defaultPart === 'want' ? c.defaultPart : 'mixed'] : null;
    const parent = c.parentId && shown.some(p => p.id === c.parentId) ? c.parentId : 'root';
    return <div className={`category-node${drag === c.id ? ' dragging' : ''}`} key={c.id} data-list={parent} data-id={c.id}>
      <div className="categoryitem">
        <button className="category-select" disabled={reorder || (pickParent ? !parentIds.has(c.id) : !manage && ((!!c.archived && !filterMode) || viewKind !== kind))} onClick={() => choose(c)}>
          <CategoryGlyph icon={c.icon} color={colorOf(c)} size={depth > 1 ? 's' : 'm'} />
          <span className="catname"><b>{c.name}{isNewCategory(c.id) && <> <span className="newtag">новая</span></>}{(pickParent ? c.id === parentId : c.id === value) && <> <Icon name="check" size={13} /></>}</b>
            {(tag || note) && <small>{tag && <span className={`parttag c-${tag[1]}`}>{tag[0]}</span>}{tag && note ? ' · ' : ''}{note}</small>}</span>
        </button>
        {reorder
          ? <button {...grip(c)}><Icon name="grip-lines" size={16} /></button>
          : <>
            {children.length > 0
              ? <button className="category-expand" aria-label={`Раскрыть ${c.name}`} aria-expanded={isOpen} onClick={() => setExpanded(switchSet(expanded, c.id))}><Icon name={isOpen ? 'chevron-down' : 'chevron-right'} size={13} /></button>
              : <span className="category-indent" />}
            {pickParent ? <span className="category-edit" aria-hidden="true" /> : actions(c)}
          </>}
      </div>
      {isOpen && children.length > 0 && <div className="category-children">{children.map(child => row(child, depth + 1, nextPath))}</div>}
    </div>;
  };
  return <>
    {/*
      В «Ещё» — обычная строка со стрелкой, как соседние (22.10.2026): подпись
      мелким серым и «Управлять» крупным чёрным читались разными шрифтами, а
      стрелка и так говорит, что строка открывается.
    */}
    {manage && !trigger ? <button className="li w" onClick={() => { setViewKind(kind); setOpen(true); }}>
      <div className="nm"><b>{kind === 'income' ? 'Категории приходов' : 'Категории расходов'}</b></div>
      <span className="ch"><Icon name="chevron-right" size={13} /></span>
    </button>
    : trigger ? trigger(() => { setViewKind(kind); setOpen(true); }, current) : <button className="oprow categoryfield" onClick={() => { setViewKind(kind); setOpen(true); }}>
      <span>{manage ? (kind === 'income' ? 'Категории приходов' : 'Категории расходов') : label}</span>
      {/* Значок стоит слева от названия — как в строке счёта: две соседние
          строки формы не должны читаться зеркально. */}
      <span className="category-value">
        {manage ? 'Управлять' : current ? <><CategoryGlyph icon={current.icon} color={colorOf(current)} size="s" /><span className="catvalue-name">{current.name}{current.archived ? ' · архив' : ''}{isNew && <> <span className="newtag">новая</span></>}</span></> : 'Выбрать'}
        <span className="catvalue-chevron" aria-hidden="true"><Icon name="chevron-right" size={13} /></span>
      </span>
    </button>}
    {open && <SheetWrap
      title={pickParent ? 'Родительская категория' : editing ? (editing === 'new' ? 'Новая категория' : name.trim() || editing.name)
        : manage ? 'Категории' : kind === 'income' ? 'Категория поступления' : 'Категория расхода'}
      onClose={() => { setOpen(false); setEditing(null); setPickParent(false); }}
      {...(pickParent ? { onBack: () => { setPickParent(false); setSearch(''); }, backLabel: 'Назад к категории' } : editing ? { onBack: () => setEditing(null), backLabel: 'Назад к категориям' } : {})}
      /* Форма — как у счёта (02.11.2026): серый лист, строки в белой карточке,
         «Сохранить» внизу. В шапке кнопка резала заголовок, а (i) про архив
         стоял у кнопки, хотя говорил об архивировании ниже. */
      {...(editing && !pickParent ? { className: 'rcpt' } : {})}>
      <div className="category-browser">
      {editing && !pickParent ? <div className="rcform catform">
        <div className="rccard rcsel">
          <CardInput label="Название" value={name} onChange={setName} placeholder="Например, Покупки" width={180} />
          <CardLook icon={icon} color={color} forAccount={false}
            onChange={(i, c) => { setIcon(i); if (c !== color) { setColor(c); setColorPicked(true); } }} />
          {viewKind === 'expense' && <CardPick label="Часть плана" tip={PART_TIP_CATEGORY} title="Часть плана категории" value={part ?? ''}
            onChange={v => { const next = v === 'need' ? 'need' : v === 'want' ? 'want' : null; setPart(next); if (!colorPicked) setColor(colorOf({ defaultPart: next, kind: viewKind } as Category)); }}
            lead={PART_QUESTION} sheetTip={PART_TIP_SHEET}
            options={[...PART_OPTIONS, PART_MIXED]} />}
          {/* Родитель — тем же списком категорий, шагом в этом листе. */}
          <button type="button" className="rcselrow" aria-label={`Входит в: ${parentCat?.name ?? 'никуда'}`} onClick={openParentPick}>
            <span className="rclabel">Входит в</span>
            <span className="rcvalue">
              {parentCat
                ? <span className="rcvalline"><CategoryGlyph icon={parentCat.icon} color={colorOf(parentCat)} size="xs" /><span className="rcvaltext">{parentCat.name}</span></span>
                : <span className="rcvaltext rcnone">никуда</span>}
              <Icon name="chevron-right" size={14} className="rcchev" />
            </span>
          </button>
        </div>
        {error && <p role="alert">{error}</p>}
        <button className="btn pri" disabled={busy || !name.trim()} onClick={() => void persist()}>{editing === 'new' ? 'Добавить' : 'Сохранить'}</button>
        {editing !== 'new' && <div className="category-form-acts">
          {/* Архивировать и удалить — тихим текстом: это не главное действие формы. */}
          {editing.archived
            ? <button className="textact" disabled={busy} onClick={() => void persist(false)}>Восстановить категорию</button>
            : <button className="textact" disabled={busy} onClick={() => void persist(true)}>{editingBranch.size > 1 ? `Архивировать ветку (${editingBranch.size})` : 'Архивировать категорию'}</button>}
          {/* Удалить — только пустую: у использованной история держится на ней. */}
          {deletable && (deleteConfirm
            ? <div className="note warn">Удалить «{editing.name}» насовсем? Операций с ней нет — ничего не потеряется.
              <button className="textact danger" disabled={busy} onClick={() => void remove()}>Удалить</button>
              <button className="textact" onClick={() => setDeleteConfirm(false)}>Отмена</button></div>
            : <button className="textact danger" onClick={() => setDeleteConfirm(true)}>Удалить категорию</button>)}
          <p className="note">Архив сохраняет операции. Вложенные категории уходят в архив вместе с родителем и возвращаются по одной.</p>
        </div>}
      </div> : <>
        {/* Сверху — вид и «+» справа; поиск — своей строкой над списком
            (решение владельца 28.10.2026). */}
        {!reorder && !pickParent && <div className="cathead">
          <div className="category-tabs" role="tablist" aria-label="Тип категорий">{(['expense','income'] as const).map(k => <button role="tab" aria-selected={viewKind === k} key={k} onClick={() => { setViewKind(k); setSearch(''); setArchive(false); }}>{k === 'expense' ? 'Расходы' : 'Доходы'}</button>)}</div>
          <button className="category-add icobtn" aria-label="Создать категорию" onClick={() => start('new')}><Icon name="plus" size={18} /></button>
        </div>}
        {!reorder && !pickParent && manage && manageHead && viewKind === 'expense' && !search && !archive && manageHead}
        {!manage && !pickParent && viewKind !== kind && <p className="note">{otherKindNote ?? `Здесь можно управлять категориями. Чтобы выбрать категорию ${viewKind === 'income' ? 'дохода' : 'расхода'}, сначала измени тип операции.`}</p>}
        {/* Частые — своим блоком плитками, как в записи: прежде строка мелких
            плашек сливалась со списком. */}
        {!reorder && !pickParent && !search && !archive && quick.length > 0 && <section className="frequent-categories">
          <div className="freq-title">{fresh ? 'Популярные' : 'Часто'}</div>
          <div className="freq-tiles">{quick.map(c => <button key={c.id} className={`opcat${c.id === value ? ' on' : ''}`} aria-pressed={c.id === value} disabled={!manage && viewKind !== kind} onClick={() => choose(c)}>
            <CategoryGlyph icon={c.icon} color={colorOf(c)} /><span>{c.name}</span></button>)}</div>
        </section>}
        {!reorder && <div className="search-field quiet catsearch">
          <SearchIcon />
          <input type="search" aria-label="Найти категорию" placeholder="Найти категорию" value={search} onChange={e => setSearch(e.target.value)} />
          {search && <button aria-label="Очистить поиск" onClick={() => setSearch('')}><CloseX /></button>}
        </div>}
        <div className="categorytools">
          <span>{reorder ? 'Потяни за полоски — выше или ниже' : archive ? 'Архивные категории' : 'Все категории'}</span>
          {reorder
            ? <button className="cattools-done" onClick={() => setReorder(false)}>Готово</button>
            : !search && !pickParent && <span className="cattools-acts">
              {!archive && list.length > 1 && <button className="cattools-ico" aria-label="Порядок" onClick={() => { setOrder(list.map(c => c.id)); setReorder(true); }}><Icon name="arrow-up-arrow-down" size={15} /></button>}
              <button className="lnk" onClick={() => setArchive(!archive)}>{archive ? 'Активные' : `Архив (${archivedCount})`}</button>
            </span>}
        </div>
        {pickParent && !search && <div className="fieldlist"><div className="categoryitem">
          <button className="category-select" onClick={() => { setParentId(''); setPickParent(false); }}>
            <CategoryGlyph icon="layer-group" color="gray" />
            <span className="catname"><b>Без родительской категории{!parentId && <> <Icon name="check" size={13} /></>}</b></span>
          </button>
          <span className="category-indent" />
        </div></div>}
        {!reorder && !pickParent && anyLabel && !search && !archive && viewKind === kind && <div className="fieldlist"><div className="categoryitem">
          <button className="category-select" onClick={() => { onChange(''); setOpen(false); }}>
            <CategoryGlyph icon="layer-group" color="gray" />
            <span className="catname"><b>{anyLabel}{!value && !pickedPart && <> <Icon name="check" size={13} /></>}</b></span>
          </button>
          <span className="category-indent" />
        </div>
        {/* Фильтр «Аналитики» отбирает и целую часть плана. */}
        {onPart && viewKind === 'expense' && ([['need', 'Все повседневные нужды', 'basket-shopping-check', 'green'], ['want', 'Все свободные траты', 'bag-shopping-heart', 'pink']] as const).map(([key, title, ico, col]) => (
          <div className="categoryitem" key={key}>
            <button className="category-select" onClick={() => { onPart(key); setOpen(false); }}>
              <CategoryGlyph icon={ico} color={col} />
              <span className="catname"><b>{title}{pickedPart === key && !value && <> <Icon name="check" size={13} /></>}</b></span>
            </button>
            <span className="category-indent" />
          </div>))}
        </div>}
        <div className={`fieldlist${reorder ? ' cat-reorder' : ''}`} ref={box}>
        {search ? list.filter(c => categoryKey(c.name).includes(categoryKey(search))).map(c => <div className="categoryitem" key={c.id}>
          <button className="category-select" disabled={pickParent ? !parentIds.has(c.id) : !manage && ((c.archived && !filterMode) || viewKind !== kind)} onClick={() => choose(c)}>
            <CategoryGlyph icon={c.icon} color={colorOf(c)} />
            <span className="catname"><b>{c.name}{isNewCategory(c.id) && <> <span className="newtag">новая</span></>}</b><small>{categories.find(p => p.id === c.parentId)?.name}</small></span>
          </button>
          {pickParent ? <span className="category-edit" aria-hidden="true" /> : actions(c)}
        </div>) : roots.map(c => row(c, 1, new Set()))}
        {(!list.length || (search && !list.some(c => categoryKey(c.name).includes(categoryKey(search))))) && <p>Категорий не найдено</p>}
        </div>
      </>}
      </div>
    </SheetWrap>}
  </>;
}

/** Три точки: строка списка правится, но правка не главное её действие. */
const DotsIcon = () => <Icon name="ellipsis-vertical" size={17} />;

const SearchIcon = () => <Icon name="magnifying-glass" size={17} />;

export function ArithmeticIcon({ symbol }: { symbol: string }) {
  const name = symbol === '+' ? 'plus' : symbol === '−' ? 'minus' : symbol === '×' ? 'xmark' : 'divide';
  return <Icon name={name} size={20} />;
}

/** Варианты строками в листе снизу — как выбор счёта; выбранный с галочкой. */
/**
 * Вариант выбора. `sub` — пояснение мелкой серой строкой (22.10.2026);
 * `icon` и `tone` — значок слева и кружок справа, как в фильтре операций;
 * `answer` — ответ на вопрос `lead` словами человека (02.11.2026).
 */
export type ChoiceOption = { id: string; title: string; sub?: string; icon?: string; tone?: string; answer?: string };

export function ChoiceSheet({ title, value, options, lead, tip, confirm, footer, onPick, onClose }: {
  title: string;
  /**
   * Выбор подтверждается кнопкой, а не тапом (03.11.2026, «Что с платежом?»):
   * у ответа последствия для денег, и случайное касание не должно их запускать.
   * Подпись кнопки — по выбранному варианту.
   */
  confirm?: (id: string) => string;
  /** Под кнопкой: ссылка на другое действие, не ответ на вопрос. */
  footer?: ReactNode;
  /** Пояснение — значок (i) у заголовка листа (02.11.2026). */
  tip?: ReactNode;
  value: string;
  /** Вопрос над вариантами: как выбрать (28.10.2026). */
  lead?: ReactNode;
  options: ChoiceOption[];
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const icons = options.some((o) => o.icon);
  const [sel, setSel] = useState(value);
  const on = confirm ? sel : value;
  return (
    <SheetWrap title={title} onClose={onClose} {...(tip ? { tip } : {})}>
      {lead && <p className={`choice-lead${icons ? ' q' : ''}`}>{lead}</p>}
      <div className={`fieldlist choice-list${icons ? ' choice-icons' : ''}`}>
        {options.map((o) => (
          <button key={o.id} className={`li w${o.id === on ? ' on' : ''}`} aria-pressed={o.id === on} data-value={o.id}
            {...(o.tone ? { style: { ['--cat' as string]: o.tone } } : {})}
            onClick={() => { if (confirm) { setSel(o.id); return; } onPick(o.id); onClose(); }}>
            {o.icon && <span className="catico choice-ico"><Icon name={o.icon} size={18} /></span>}
            <span className="nm">{o.answer && <em className="choice-answer">{o.answer}</em>}<span>{o.title}</span>{o.sub && <small className="choice-sub">{o.sub}</small>}</span>
            {icons
              ? <span className={`ops-radio${o.id === on ? ' on' : ''}`} aria-hidden="true">{o.id === on && <Icon name="check" size={15} />}</span>
              : <span>{o.id === on ? <Icon name="check" size={14} /> : null}</span>}
          </button>
        ))}
      </div>
      {confirm && (
        <button type="button" className="btn pri choice-go" disabled={!sel} onClick={() => { onPick(sel); onClose(); }}>
          {sel ? confirm(sel) : 'Выбери ответ'}</button>
      )}
      {footer}
    </SheetWrap>
  );
}

/**
 * Строка выбора: подпись слева, значение справа; тап по значению — варианты
 * строками в листе снизу. Системный список был чужим на вид и на iPhone
 * закрывал форму колесом (16.10.2026).
 */
export function PickRow({ label, value, onChange, options, tip, sheetTip = tip, lead, warn = false, empty = '—' }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ChoiceOption[];
  lead?: ReactNode;
  tip?: ReactNode;
  /** Пояснение в листе выбора, если там нужно другое (по умолчанию — `tip`). */
  sheetTip?: ReactNode;
  /** Значение надо выбрать самому: строка подсвечена, пока не выбрано. */
  warn?: boolean;
  /** Что написано, пока ничего не выбрано. */
  empty?: string;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <>
      <div className={`oprow${warn ? ' warn' : ''}`}><span>{label}{tip ? <Tip>{tip}</Tip> : null}</span>
        <button type="button" className="opval" aria-label={`${label}: ${current?.title ?? empty}`} onClick={() => setOpen(true)}>
          {/* Значок варианта — как в листе выбора, мелко и без подложки (02.11.2026). */}
          {current?.icon && <span className="opvalico" style={{ color: current.tone }}><Icon name={current.icon} size={14} /></span>}
          <span className="opvaltext">{current?.title ?? empty}</span>
        </button>
      </div>
      {open && <ChoiceSheet title={label} value={value} options={options} {...(lead ? { lead } : {})} {...(sheetTip ? { tip: sheetTip } : {})} onPick={onChange} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Поле выбора в форме: выглядит как поле ввода, тап — варианты листом. */
export function SelectField({ label, value, onChange, options, lead, tip }: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: ChoiceOption[];
  lead?: ReactNode;
  tip?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  return (
    <>
      <button type="button" className="fieldbtn" aria-label={`${label}: ${current?.title ?? 'не выбрано'}`} onClick={() => setOpen(true)}>
        <span>{current?.title ?? 'Выбрать'}</span><Icon name="chevron-down" size={13} />
      </button>
      {open && <ChoiceSheet title={label} value={value} options={options} {...(lead ? { lead } : {})} {...(tip ? { tip } : {})} onPick={onChange} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * Что приход сделает с ожидаемым доходом — до записи, а не после.
 *
 * Разовые деньги не закрывают ожидаемую зарплату и не становятся регулярным
 * доходом: посчитать подарок зарплатой значит пообещать человеку его
 * повторение в следующем месяце.
 *
 * Пришло меньше, чем ждали, — нужен ответ человека: по одной сумме нельзя
 * понять, задержали половину зарплаты или месяц просто выдался такой.
 */
export function IncomeLink({ stream, value, waitRest, onWaitRest, pending = null }: {
  stream: { name: string; amount: Money } | undefined;
  value: Money;
  waitRest: boolean | null;
  onWaitRest: (wait: boolean) => void;
  /**
   * Сколько ещё ждут по сроку, если по нему уже пришла часть, — от этого
   * «часть» и «Жду …», а не от всей суммы дохода.
   */
  pending?: Money | null;
}) {
  if (value <= ZERO) return null;
  const base = pending ?? stream?.amount ?? ZERO;
  const partial = !!stream && value < base;
  return (
    <>
      <div className="note">
        {stream
          ? partial
            ? pending !== null
              ? `Это меньше остатка, который ещё ждали от «${stream.name}» (${formatMoney(pending)}).`
              : `Это меньше, чем ждали от «${stream.name}» (${formatMoney(stream.amount)}).`
            : `Закроет ожидаемый доход «${stream.name}» — второй раз в прогнозе он не появится.`
          : 'Разовые деньги: ожидаемый доход они не закрывают и регулярным доходом не станут.'}
      </div>
      {partial && stream && (
        <>
          <div className="pick">
            <div className="k">Ждёшь остаток?
              <Tip>{waitRest === true
                ? 'Остаток останется в прогнозе отдельной строкой — как ожидание, а не как деньги.'
                : waitRest === false
                  ? 'Ожидание закроется: в этом месяце по этому доходу больше ничего не ждём.'
                  : 'Без ответа приложение не станет решать само: и «ждёт», и «не ждёт» меняют прогноз.'}</Tip>
            </div>
            <div className="chips">
              <button className={`chip${waitRest === true ? ' on' : ''}`} onClick={() => onWaitRest(true)}>
                Жду {formatMoney(sub(base, value))}
              </button>
              <button className={`chip${waitRest === false ? ' on' : ''}`} onClick={() => onWaitRest(false)}>
                Это всё
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/**
 * Заметка — растёт по тексту до четырёх строк и переносит слова
 * (28.10.2026): в строке ввода длинная фраза, сказанная голосом, уходила за
 * край. Enter, как раньше, возвращает к сумме: заметка — подпись, а не письмо.
 */
export function NoteField({ value, onChange, inputRef, onFocus, onBlur, onEnter }: {
  value: string; onChange: (v: string) => void; inputRef: RefObject<HTMLTextAreaElement>;
  onFocus: () => void; onBlur: () => void; onEnter: () => void;
}) {
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value, inputRef]);
  return (
    <div className="oprow noterow">
      <textarea className="operation-note" rows={1} aria-label="Заметка" placeholder="Заметка" value={value} maxLength={1000}
        ref={inputRef} onFocus={onFocus} onBlur={onBlur}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnter(); } }}
        onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
